import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global hook that continuously shares user's location when Rider Radar is enabled.
 * Uses navigator.geolocation.watchPosition for real-time tracking.
 * Runs at App level so it works across all pages.
 */
export function useRiderRadar() {
  const { user } = useAuth();
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const isTrackingRef = useRef(false);

  useEffect(() => {
    if (!user || !navigator.geolocation) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const authHeaders = { Authorization: `Bearer ${token}` };

    const stopWatching = () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      isTrackingRef.current = false;
    };

    const startWatching = () => {
      if (isTrackingRef.current) return;
      isTrackingRef.current = true;

      watchIdRef.current = navigator.geolocation.watchPosition(
        async (pos) => {
          try {
            await axios.post(`${API_URL}/rider-radar/update-location`, {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              radar_visible: true
            }, { headers: authHeaders });
          } catch {}
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
      );
    };

    const checkAndStart = async () => {
      try {
        const res = await axios.get(`${API_URL}/rider-radar/my-status`, { headers: authHeaders });
        if (res.data?.radar_visible) {
          startWatching();
        } else {
          stopWatching();
        }
      } catch {
        stopWatching();
      }
    };

    checkAndStart();

    // Re-check every 30s in case user toggled radar from settings
    intervalRef.current = setInterval(checkAndStart, 30000);

    return () => {
      stopWatching();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user]);
}
